import { CardImage, H5 } from "@sellar-io/components";
import { DiscoverPreOrderProductType } from "@sellar-io/api-v2/types";

import { twMerge } from "tailwind-merge";
import { DietaryDisplayList } from "../../../../components/dietaryDisplayList/DietaryDisplayList";

import { ImageSlider } from "../../../../components/imageSlider/ImageSlider";
import { useMediaQuery } from "usehooks-ts";
import { Icon } from "../../../../components";
import dayjs from "dayjs";
import { TooltipTrigger } from "@sellar-io/components";
import { Tooltip } from "@sellar-io/components";
import { TooltipContent } from "@sellar-io/components";

import { ParentLink } from "../../../../components/navigation/ParentLink";

export const DiscoveryPreOrderProductCard = ({
  product,
  businessName,
  logoUrl,
  className,
  onClick,
}: {
  product: DiscoverPreOrderProductType;
  businessName: string;
  logoUrl: string;
  className?: string;
  onClick: () => void;
}) => {
  let productHeroImages = product.ProductImages.filter(
    (ProductImage) => !!ProductImage.url,
  ).map((ProductImage) => ProductImage.url);

  if (productHeroImages.length === 0 && product.heroImageUrl)
    productHeroImages = [product.heroImageUrl];

  const hasProductHeroImages =
    productHeroImages && productHeroImages.length > 0;

  const isDesktop = useMediaQuery("(min-width: 770px)");

  return (
    <div
      className={
        "flex min-w-[250px] flex-col gap-4 overflow-hidden rounded-lg border border-zinc-300"
      }
    >
      <div
        key={product.id}
        className={twMerge("flex h-full cursor-pointer flex-col", className)}
        onClick={onClick}
      >
        <div className="relative h-40 min-w-[250px] cursor-pointer overflow-clip bg-white">
          {hasProductHeroImages ? (
            <ImageSlider
              images={productHeroImages}
              hoverAnimate={true}
              imageClassName="pt-40"
              swipe={isDesktop}
            />
          ) : (
            <div className="relative h-40 min-w-[250px] cursor-pointer overflow-clip rounded-lg bg-white">
              <CardImage
                imageUrl={product.imageUrl ?? logoUrl}
                fallback="product-hero"
                className={"h-40 w-[250px] blur-lg"}
              />
              <div
                className={
                  "absolute inset-0 flex items-center justify-center text-white"
                }
              >
                <img
                  src={product.imageUrl ?? logoUrl}
                  alt={businessName}
                  height="100"
                  width="100"
                  className="rounded-full"
                />
              </div>
            </div>
          )}
          {product.preOrderAvailableFromDates.length > 0 && (
            <PreOrderPill
              availableFromDates={product.preOrderAvailableFromDates}
            />
          )}
        </div>
        <div className="mt-2 flex grow flex-col justify-between">
          <div className="px-2 pb-1">
            <p className={"text-sm font-semibold text-zinc-800"}>
              {product.name}{" "}
              <DietaryDisplayList
                dietaryRequirements={{
                  VG: Boolean(product.vegan),
                  GF: Boolean(product.glutenFree),
                  LF: Boolean(product.lactoseFree),
                }}
                mode="text"
              />
            </p>
            <p className={"text-xs text-zinc-500"}>
              {[product.kind, `${product.abv}%`].join(" // ")}
            </p>
          </div>
          <p className="mt-2 border-t border-[#D4D4D8] bg-zinc-50 px-2 py-1.5 text-xs font-bold text-zinc-500">
            &nbsp;
            {product.containerTypes.join(" • ").toUpperCase()}
          </p>
        </div>
      </div>
    </div>
  );
};

export const DiscoveryPreOrderLinkCard = ({
  supplierId,
  businessName,
  logoUrl,
  supplierUrl,
}: {
  supplierId: number;
  businessName: string;
  logoUrl: string;
  supplierUrl: string;
}) => {
  return (
    <div
      className={
        "flex min-w-[250px] flex-col gap-4 overflow-hidden rounded-lg border border-zinc-300 bg-white"
      }
    >
      <ParentLink
        key={supplierId}
        className={"flex h-full cursor-pointer flex-col"}
        to={supplierUrl}
      >
        <div className="relative h-full min-w-[250px] cursor-pointer overflow-clip bg-white">
          <div className="flex h-full flex-col items-center justify-center">
            <img
              src={logoUrl}
              alt={businessName}
              height="100"
              width="100"
              className="rounded-full"
            />
            <H5 className="bold mt-2 text-sm">{businessName}</H5>
            <p className="mt-5 flex text-sm font-semibold text-neutral-800 text-zinc-800">
              GO TO STOREFRONT <Icon icon={"CHEVRON_RIGHT"} color={"#27272A"} />
            </p>
          </div>
        </div>
      </ParentLink>
    </div>
  );
};

export const PreOrderPill = ({
  availableFromDates,
}: {
  availableFromDates: Date[];
}) => (
  <div className="absolute top-2 right-2 left-2 flex">
    {availableFromDates.length > 0 && (
      <div className="flex inline-flex h-[32px] flex-row items-center justify-start gap-1 rounded-[100px] border border-indigo-500 bg-[#EEF2FF] p-1 pr-2">
        <Icon size={1} icon={"PROGRESS_CLOCK"} color={"#27272A"} />
        <p
          className={
            "overflow-hidden text-sm font-semibold text-nowrap text-ellipsis text-zinc-800"
          }
        >
          From: {dayjs(availableFromDates[0]).format("DD/MM/YYYY")}
        </p>
      </div>
    )}
  </div>
);

export const PreOrderPillToolTip = () => (
  <div className="absolute top-3 right-4 ms-0 flex">
    <Tooltip placement="bottom">
      <TooltipTrigger>
        <div className="flex inline-flex h-[32px] flex-row items-center justify-start gap-1 rounded-s-full border bg-[#EEF2FF] p-1 pr-2">
          <Icon size={0.75} icon={"PROGRESS_CLOCK"} color={"#3730A3"} />
          <p
            className={
              "overflow-hidden text-sm font-medium text-nowrap text-ellipsis text-[#3730A3]"
            }
          >
            Pre-order
          </p>
        </div>
        <div className="ms-[1px] flex inline-flex h-[32px] flex-row items-center justify-start gap-1 rounded-e-full border bg-[#EEF2FF] p-1 pr-2">
          <Icon size={0.75} icon={"INFORMATION_OUTLINE"} color={"#27272A"} />
        </div>
      </TooltipTrigger>
      <TooltipContent
        className={
          "Tooltip w-[300px] rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-lg"
        }
      >
        <div className="flex gap-2">
          <div className="flex shrink-0 items-center">
            <Icon size={0.75} icon={"INFORMATION_OUTLINE"} color={"#27272A"} />
          </div>
          <div>
            <H5 className="text-sm font-semibold text-zinc-800">
              Variable Dates
            </H5>
            <p className="text-xs text-zinc-700">
              Each pre-order product can have one or more dates depending on the
              number of container types.
            </p>
          </div>
        </div>
        {/* Tooltip tail - TODO: implement this so it points to the info icon */}
        {/* <div className="absolute left-3/4 top-[-7px] -translate-x-1/2">
          <div className="h-3 w-3 rotate-45 transform border-l border-t border-gray-300 bg-white"></div>
        </div> */}
      </TooltipContent>
    </Tooltip>
  </div>
);
